import React from 'react';
import { Link } from 'react-router-dom';
import styled, { DefaultTheme } from 'styled-components';
import Row from '../components/Row';
import Text from '../components/Text';
import SvgFragment from './SvgFragment';
import { AppRoutes } from '../interfaces/store/appStore';
import { CardExpiryBannerState } from '../utils/cardExpiry';
import { styles } from '../styles/themes/style';

interface CardExpiryBannerProps {
  state: CardExpiryBannerState | null;
}

const CardExpiryBanner: React.FC<CardExpiryBannerProps> = ({ state }) => {
  if (!state) return null;
  const { variant, daysRemaining, last4 } = state;
  const isExpired = variant === 'expired';

  const detail = isExpired
    ? 'A payment card on file has expired — renewals may fail until you update.'
    : daysRemaining === 0
      ? 'Your payment card expires today — update to avoid subscription issues.'
      : `Your payment card expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} — update to avoid interruptions.`;

  return (
    <Container $variant={variant}>
      <InnerRow alignItems gap="10px">
        <IconWrap $variant={variant}>
          <SvgFragment type={isExpired ? 'warning' : 'card'} />
        </IconWrap>
        <LineWrap>
          <Text $paragraph2 $bold>{isExpired ? 'Card expired' : 'Card expiring soon'}</Text>
          {last4 ? (<Text $paragraph2 $lighter>(•••• {last4})</Text>) : null}
          <Text $paragraph2>. {detail}</Text>
          <Link
            to={AppRoutes.SETTINGS_BILLING}
            state={{ billingTab: 'paymentMethods' }}
            style={{ textDecoration: 'none' }}
          >
            <Text $paragraph2 $active>
              Update payment method
            </Text>
          </Link>
        </LineWrap>
      </InnerRow>
    </Container>
  );
};

function isLightTheme(theme: DefaultTheme): boolean {
  return (theme as { primary?: boolean }).primary !== false;
}

function bannerBackground(theme: DefaultTheme, variant: 'expired' | 'expiring_soon'): string {
  if (isLightTheme(theme)) {
    return variant === 'expired' ? styles.colors.red200 : styles.colors.yellow200;
  }
  return variant === 'expired' ? 'rgba(255, 103, 96, 0.14)' : 'rgba(253, 190, 0, 0.12)';
}

const Container = styled.div<{ $variant: 'expired' | 'expiring_soon' }>`
  width: 100%;
  min-height: 40px;
  padding: 10px 16px;
  background: ${({ theme, $variant }) => bannerBackground(theme, $variant)};
  border-left: 4px solid
    ${({ $variant }) =>
      $variant === 'expired' ? styles.colors.red600 : styles.colors.orange600};
`;

const InnerRow = styled(Row)`
  margin: 0 auto;
`;

const LineWrap = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 6px;
  row-gap: 2px;
`;

const IconWrap = styled.span<{ $variant: 'expired' | 'expiring_soon' }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ $variant }) =>
    $variant === 'expired' ? styles.colors.red300 : styles.colors.yellow300};

  .svg .path {
    fill: ${({ $variant }) =>
      $variant === 'expired' ? styles.colors.red600 : styles.colors.black500};
  }
`;

export default CardExpiryBanner;
